import { Box, Button, Menu, MenuItem, Table, Typography, Select, FormControl } from "flux";
import { InputLabel } from "@mui/material";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { useTableSearch } from "@adora/shared/hooks/use-table-search";
import CreateUserDialog from "../../components/create-user-dialog";
import BulkCreateUsersDialog from "../../components/bulk-create-users-dialog";
import { User, UserAccountType } from "../../types/user";
import EditUserDialog from "../edit-user-dialog";
import { useState, useCallback, useMemo } from "react";
import ConfirmationDialog from "@adora/shared/components/confirm-dialog";
import useMutateUser from "../../hooks/use-mutate-user";
import Page from "@adora/shared/components/layout/page";
import useBrands from "../../hooks/use-brands";

function getAccountTypeLabel(user: User): string {
  if (user.accountType === "media") return "Media";
  if (user.accountType === "app" && user.role === "customer") return "Customer";
  if (user.accountType === "internal" && user.role === "internal") return "Admin UI";
  return "Internal";
}

const StatusBadge: React.FC<{ isDeleted?: boolean }> = ({ isDeleted }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: "8px",
      py: "2px",
      borderRadius: "12px",
      bgcolor: isDeleted ? "#ef444420" : "#22c55e20",
      color: isDeleted ? "#ef4444" : "#22c55e",
      fontSize: "12px",
      fontWeight: 600
    }}
  >
    {isDeleted ? "Deleted" : "Active"}
  </Box>
);

function escapeCSVField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

export interface UsersPageProps {
  title: string;
  type: UserAccountType;
  users?: User[];
  isLoadingUsers: boolean;
  brandName?: string;
  brandId?: string;
}

const UsersPage: React.FC<UsersPageProps> = ({ title, type, users, isLoadingUsers, brandName, brandId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedAccountType, setSelectedAccountType] = useState<string | null>("all");
  const [selectedBrand, setSelectedBrand] = useState<string | null>("all");
  const [selectedStatus, setSelectedStatus] = useState<string | null>("all");
  const { searchValue, debouncedSearchValue, setSearchValue } = useTableSearch({ debounceMs: 150 });

  const { remove, restore } = useMutateUser();
  const { openDialog } = useDialog();
  const { brands } = useBrands();

  const brandsByIdMap = useMemo(
    () => Object.fromEntries(brands?.map((brand) => [brand.id, brand.name]) || []),
    [brands]
  );

  const filteredUsers = useMemo(
    () =>
      users?.filter((user) => {
        const accountTypeMatch =
          selectedAccountType === "all" ||
          (selectedAccountType === "internal" && user.accountType === "app" && user.role === "internal") ||
          (selectedAccountType === "customer" && user.accountType === "app" && user.role === "customer") ||
          (selectedAccountType === "admin" && user.accountType === "internal" && user.role === "internal") ||
          (selectedAccountType === "media" && user.accountType === "media");

        const brandMatch = selectedBrand === "all" || user.brands?.includes(Number(selectedBrand));

        const statusMatch =
          selectedStatus === "all" ||
          (selectedStatus === "active" && !user.isDeleted) ||
          (selectedStatus === "deleted" && !!user.isDeleted);

        const nameMatch =
          debouncedSearchValue === "" || user.name.toLowerCase().includes(debouncedSearchValue.toLowerCase());

        return accountTypeMatch && brandMatch && statusMatch && nameMatch;
      }),
    [users, selectedAccountType, selectedBrand, selectedStatus, debouncedSearchValue]
  );

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedUser(null);
  }, []);

  const handleDownloadCsv = useCallback(() => {
    if (!filteredUsers?.length) return;
    const header = ["ID", "Name", "Email", "Account Type", "Status", "Brands"];
    const rows = filteredUsers.map((user) => [
      String(user.id),
      escapeCSVField(user.name),
      escapeCSVField(user.email ?? ""),
      getAccountTypeLabel(user),
      user.isDeleted ? "Deleted" : "Active",
      escapeCSVField(user.brands?.map((id) => brandsByIdMap[id] || String(id)).join("; ") ?? "")
    ]);
    const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users.csv";
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredUsers, brandsByIdMap]);

  const handleBulkCreateUsers = async () => {
    await openDialog(BulkCreateUsersDialog, {});
  };

  const handleCreateUser = async () => {
    await openDialog(CreateUserDialog, {
      brandId: brandId?.toString(),
      type,
      brands: brands || []
    });
  };

  const handleEditUser = useCallback(async () => {
    if (selectedUser) {
      await openDialog(EditUserDialog, { user: selectedUser });
    }
    handleMenuClose();
  }, [selectedUser, openDialog, handleMenuClose]);

  const handleDeleteUser = useCallback(async () => {
    if (selectedUser) {
      const confirm = await openDialog(ConfirmationDialog, {
        title: "Delete User?",
        description:
          "This will mark this user as deleted and revoke their access. You can restore them later with Un-Delete. Are you sure?",
        primaryButtonText: "Delete",
        danger: true
      });
      if (confirm) {
        remove(selectedUser.id);
      }
    }
    handleMenuClose();
  }, [selectedUser, openDialog, handleMenuClose, remove]);

  const handleUnDeleteUser = useCallback(async () => {
    if (selectedUser) {
      const confirm = await openDialog(ConfirmationDialog, {
        title: "Un-Delete User?",
        description: "This will restore this user and make them active again. Are you sure?",
        primaryButtonText: "Un-Delete"
      });
      if (confirm) {
        restore(selectedUser.id);
      }
    }
    handleMenuClose();
  }, [selectedUser, openDialog, handleMenuClose, restore]);

  const columns = useMemo(
    () => [
      { field: "id", value: "ID", width: 100 },
      { field: "name", value: "Name", width: 400 },
      { field: "email", value: "Email", width: 300 },
      {
        field: "accountType",
        value: "Account Type",
        width: 200,
        renderCell: (user: User) => <Typography>{getAccountTypeLabel(user)}</Typography>
      },
      {
        field: "status",
        value: "Status",
        width: 120,
        renderCell: (user: User) => <StatusBadge isDeleted={user.isDeleted} />
      },
      {
        field: "brands",
        value: "Brands",
        width: 500,
        renderCell: (user: User) => (
          <Box>
            {user?.brands?.map((id) => (
              <Typography key={id}>{brandsByIdMap[id]}</Typography>
            ))}
          </Box>
        )
      },
      {
        field: "actions",
        value: "Actions",
        width: 50,
        renderCell: (user: User) => (
          <Button
            variant="text"
            color="secondary"
            size="medium"
            onClick={(e) => handleMenuOpen(e, user)}
            dataDogIdentifier="users page ellipsis action"
          >
            …
          </Button>
        )
      }
    ],
    [brandsByIdMap, handleMenuOpen]
  );

  return (
    <Page
      backLinkText={brandName}
      title={title}
      titleBarActions={
        <>
          <Button variant="outlined" onClick={handleDownloadCsv} dataDogIdentifier="users page download csv">
            Download CSV
          </Button>
          <Button variant="outlined" onClick={handleBulkCreateUsers} dataDogIdentifier="users page bulk create users">
            Bulk Create
          </Button>
          <Button variant="contained" onClick={handleCreateUser} dataDogIdentifier="users page create user">
            Create User
          </Button>
        </>
      }
    >
      <Box height="50px" display="flex" gap={2} mb={2} alignItems="flex-end">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 200,
            height: "30px",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "4px",
            "&:focus-within": {
              borderColor: "primary.main"
            }
          }}
        >
          <Box sx={{ ml: 1 }}>🔍</Box>
          <Box
            component="input"
            placeholder="Search by name"
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              height: "100%",
              border: "none",
              padding: "0 14px",
              fontSize: "14px",
              "&:focus": {
                outline: "none"
              }
            }}
          />
        </Box>

        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            "& .MuiInputLabel-root.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" }
          }}
        >
          <InputLabel>Account Type</InputLabel>
          <Select
            value={selectedAccountType}
            label="Account Type"
            onChange={(e) => setSelectedAccountType(e.target.value as string)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="media">Media</MenuItem>
            <MenuItem value="admin">Admin UI</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            "& .MuiInputLabel-root.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" }
          }}
        >
          <InputLabel>Brand</InputLabel>
          <Select value={selectedBrand} label="Brand" onChange={(e) => setSelectedBrand(e.target.value as string)}>
            <MenuItem value="all">All</MenuItem>
            {brands?.map((brand) => (
              <MenuItem key={brand.id} value={brand.id}>
                {brand.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          size="small"
          sx={{
            minWidth: 200,
            "& .MuiInputLabel-root.MuiInputLabel-shrink": { transform: "translate(14px, -9px) scale(0.75)" }
          }}
        >
          <InputLabel>Status</InputLabel>
          <Select value={selectedStatus} label="Status" onChange={(e) => setSelectedStatus(e.target.value as string)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="deleted">Deleted</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Table
        height="82vh"
        stickyHeaders={true}
        columns={columns}
        rows={filteredUsers}
        isLoading={isLoadingUsers}
        dataDogIdentifier="users"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "110px"
          }
        }}
      >
        <MenuItem size="medium" onClick={handleEditUser}>
          Edit
        </MenuItem>
        {selectedUser?.isDeleted ? (
          <MenuItem size="medium" onClick={handleUnDeleteUser}>
            Un-Delete
          </MenuItem>
        ) : (
          <MenuItem size="medium" onClick={handleDeleteUser}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </Page>
  );
};

export default UsersPage;
